import { useState, useCallback, useMemo, useRef } from 'react';
import { useSnackbar } from 'hooks/useSnackbar';
import { useRestApi } from 'hooks/useRestApi';
import { apiUrl } from 'utils/apiUrl';
import { useTranslation } from 'react-i18next';
import { Cohort, CohortDetail } from 'App.types';
import { useCohortsContext } from 'contexts/CohortsContext/CohortsContext';
import { handleApiError } from '../utils';

interface BatchPatchResponse {
  sites_successfully_updated: string[];
  sites_failed_update: string[];
}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/* eslint-disable no-await-in-loop */
const retryApiOperation = async (operation: () => Promise<any>, maxAttempts = 3): Promise<any> => {
  let lastError: any;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error;
      if (attempt === maxAttempts) {
        throw error;
      }
      await sleep(1000 * attempt);
    }
  }
  throw lastError || new Error('All retry attempts failed');
};
/* eslint-enable no-await-in-loop */

export const useCohortsActions = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { cohorts, loading, error, selectedRegion, dispatch } = useCohortsContext();

  const [loadingCreate, setLoadingCreate] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [loadingAddSite, setLoadingAddSite] = useState(false);
  const [loadingRemoveSite, setLoadingRemoveSite] = useState(false);

  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const enqueueSnackbarRef = useRef(enqueueSnackbar);
  enqueueSnackbarRef.current = enqueueSnackbar;

  const tRef = useRef(t);
  tRef.current = t;

  const { fetch: fetchCohorts } = useRestApi<{ items: Cohort[] }>(
    apiUrl('lct', `regions/${selectedRegion}/cohorts?results_count=400`),
    { manual: true },
  );
  const { doPost: apiCreateCohort } = useRestApi<{ id: string }>(
    apiUrl('lct', `regions/${selectedRegion}/cohort`),
    { manual: true },
  );
  const { doDelete: apiDeleteCohort } = useRestApi<void>(
    apiUrl('lct', `regions/${selectedRegion}/cohort`),
    { manual: true },
  );
  const { doPatch: apiUpdateCohort } = useRestApi<void>(
    apiUrl('lct', `regions/${selectedRegion}/cohort`),
    { manual: true },
  );
  const { doPatch: apiPatchSite } = useRestApi<void>(
    apiUrl('lct', `regions/${selectedRegion}/site`),
    { manual: true },
  );
  const { doPost: apiResetSite } = useRestApi<void>(
    apiUrl('lct', `regions/${selectedRegion}/site`),
    { manual: true },
  );
  const { doPatchBatch: apiPatchSitesBatch } = useRestApi<BatchPatchResponse>(
    apiUrl('lct', `regions/${selectedRegion}/sites`),
    { manual: true },
  );
  const { fetch: fetchSitesInCohort } = useRestApi<{ items: any[] }>(
    apiUrl('lct', `regions/${selectedRegion}/search`),
    { manual: true },
  );

  const refreshCohorts = useCallback(async (): Promise<void> => {
    try {
      dispatchRef.current({ type: 'SET_LOADING', payload: true });
      const response = await fetchCohorts();

      if (response?.data?.items) {
        dispatchRef.current({
          type: 'SET_COHORTS',
          payload: response.data.items,
        });
      } else {
        dispatchRef.current({ type: 'SET_LOADING', payload: false });
      }
    } catch (error: any) {
      if (error?.name === 'AbortError' || error?.message === 'Cancel' || !error?.message) {
        return;
      }

      dispatchRef.current({ type: 'SET_ERROR', payload: error });
      dispatchRef.current({ type: 'SET_LOADING', payload: false });
      handleApiError(error, {
        operation: 'loadCohorts',
        enqueueSnackbar: enqueueSnackbarRef.current,
        t: tRef.current,
      });
    }
  }, [fetchCohorts]);

  const assignSitesToCohort = useCallback(
    async (siteIds: string[], cohortId: string) => {
      await sleep(2000);
      await apiPatchSitesBatch({ captain_ids: siteIds, cohort: cohortId });
    },
    [apiPatchSitesBatch],
  );

  const removeSitesFromCohort = useCallback(
    async (siteIds: string[]) => {
      await apiPatchSitesBatch({ captain_ids: siteIds, cohort: 'NO_COHORT_ASSIGNED' });
      await sleep(2000);
    },
    [apiPatchSitesBatch],
  );

  const createCohort = useCallback(
    async (cohortData: Omit<Cohort, 'sites_count'>, selectedSites: string[]) => {
      try {
        setLoadingCreate(true);

        await apiCreateCohort(cohortData);

        if (selectedSites.length > 0) {
          await assignSitesToCohort(selectedSites, cohortData.id);
        }

        const newCohort = { ...cohortData, sites_count: String(selectedSites.length) };
        dispatchRef.current({ type: 'ADD_COHORT', payload: newCohort });

        try {
          await sleep(1000);
          const response = await fetchCohorts();
          if (response?.data?.items) {
            const serverCohort = response.data.items.find((c: any) => c.id === cohortData.id);
            if (serverCohort && serverCohort.created_by_user) {
              dispatchRef.current({
                type: 'UPDATE_COHORT',
                payload: {
                  id: cohortData.id,
                  updates: { created_by_user: serverCohort.created_by_user },
                },
              });
            }
          }
        } catch (syncError) {
          console.warn('Background sync failed:', syncError);
        }

        enqueueSnackbarRef.current(
          tRef.current('cohorts.successMessage', {
            name: cohortData.name || 'Cohort',
            action: 'created',
          }),
          { variant: 'success' },
        );
      } catch (error: any) {
        handleApiError(error, {
          operation: 'createCohort',
          enqueueSnackbar: enqueueSnackbarRef.current,
          t: tRef.current,
          entityName: cohortData.name,
        });
        throw error;
      } finally {
        setLoadingCreate(false);
      }
    },
    [apiCreateCohort, assignSitesToCohort, fetchCohorts],
  );

  const deleteCohort = useCallback(
    async (cohortId: string) => {
      try {
        setLoadingDelete(true);

        const sitesResponse = await fetchSitesInCohort({
          params: { cohort: cohortId, results_count: 100 },
        });

        const sitesInCohort = sitesResponse?.data?.items || [];
        const siteIds = sitesInCohort.map((s: any) => s.captain_id);

        if (siteIds.length > 0) {
          await removeSitesFromCohort(siteIds);
        }

        await retryApiOperation(async () => await apiDeleteCohort(cohortId));

        dispatchRef.current({ type: 'REMOVE_COHORT', payload: cohortId });
        enqueueSnackbarRef.current(tRef.current('cohorts.cohortDeletedSuccessfully'), {
          variant: 'success',
        });
      } catch (error: any) {
        handleApiError(error, {
          operation: 'deleteCohort',
          enqueueSnackbar: enqueueSnackbarRef.current,
          t: tRef.current,
        });
        throw error;
      } finally {
        setLoadingDelete(false);
      }
    },
    [fetchSitesInCohort, removeSitesFromCohort, apiDeleteCohort],
  );

  const addSiteToACohort = useCallback(
    async (siteId: string, cohortId: string) => {
      try {
        setLoadingAddSite(true);
        await retryApiOperation(async () => await apiPatchSite(siteId, { cohort: cohortId }));
        await sleep(2000);
        await refreshCohorts();
      } catch (error) {
        await refreshCohorts();
        throw error;
      } finally {
        setLoadingAddSite(false);
      }
    },
    [apiPatchSite, refreshCohorts],
  );

  const removeSiteFromCohort = useCallback(
    async (siteId: string) => {
      try {
        setLoadingRemoveSite(true);
        await apiResetSite({}, undefined, `${siteId}/reset`);
        await sleep(2000);
        await refreshCohorts();
      } catch (error) {
        await refreshCohorts();
        throw error;
      } finally {
        setLoadingRemoveSite(false);
      }
    },
    [apiResetSite, refreshCohorts],
  );

  const updateCohortDowntime = useCallback(
    async (cohortId: string, updates: Partial<Cohort>) => {
      try {
        setLoadingUpdate(true);
        dispatchRef.current({
          type: 'UPDATE_COHORT',
          payload: { id: cohortId, updates },
        });
        await retryApiOperation(async () => await apiUpdateCohort(cohortId, updates));
        enqueueSnackbarRef.current(tRef.current('cohorts.downtimeUpdatedSuccessfully'), {
          variant: 'success',
        });
      } catch (error: any) {
        handleApiError(error, {
          operation: 'updateDowntime',
          enqueueSnackbar: enqueueSnackbarRef.current,
          t: tRef.current,
        });
        await refreshCohorts();
        throw error;
      } finally {
        setLoadingUpdate(false);
      }
    },
    [apiUpdateCohort, refreshCohorts],
  );

  const getCohortById = useMemo(
    () =>
      (id: string): Cohort | undefined =>
        cohorts.find((cohort) => cohort.id === id),
    [cohorts],
  );

  const searchCohorts = useMemo(
    () =>
      (searchTerm: string): CohortDetail[] => {
        if (!searchTerm.trim()) return cohorts as CohortDetail[];
        const term = searchTerm.toLowerCase();
        return (cohorts as CohortDetail[]).filter(
          (cohort) =>
            cohort.name?.toLowerCase().includes(term) ||
            cohort.migration_status?.toLowerCase().includes(term) ||
            cohort.created_by_user?.toLowerCase().includes(term) ||
            cohort.status_reason?.toLowerCase().includes(term),
        );
      },
    [cohorts],
  );

  return {
    cohorts,
    loading,
    error,
    loadingCreate,
    loadingDelete,
    loadingUpdate,
    loadingAddSite,
    loadingRemoveSite,
    createCohort,
    deleteCohort,
    refreshCohorts,
    addSiteToACohort,
    removeSiteFromCohort,
    updateCohortDowntime,
    getCohortById,
    searchCohorts,
  };
};
